// Gallery Personal/Team context — pure selection + persistence helpers
// (SPEC-2026-08-18, Phase 1).
//
// PURE module (no React, no network) so it is unit-testable in isolation. Owns:
//  - the sentinel for the Personal context,
//  - reading/writing the last-selected context to localStorage (remember-last),
//  - resolving a persisted/selected context id against the CURRENT team list so
//    a team the user has since left never scopes the gallery to a dead id
//    (falls back to Personal — the fail-safe default).
//
// The stored value is always a STRING (Personal sentinel or a stringified team
// id) because IyugaDropdown option values are strings; callers coerce to the
// team's real (integer) id via `teamIdFromContext`.

export const PERSONAL_CONTEXT = "personal";
// T20.22 (Kelly, Slack 2026-10-12): the default context IS the owner's own
// gallery, so the switcher shows it as "Gallery", not "Personal". Only the
// visible label changed; the stored value stays "personal" so persisted
// selections keep resolving.
export const PERSONAL_CONTEXT_LABEL = "Gallery";

// gordon-1005-P1-gallery-collaborations-view: a 3rd context alongside Personal
// and each team — wheels OTHER people have shared with the viewer (BE
// GET /wheel/collaborations/?tab=shared_with_me). Lives in the SAME
// `selectedContext`/localStorage value as Personal/team ids (not a separate
// boolean) specifically so gordon-1005-P1-gallery-nav-resets-to-personal's
// "Gallery nav click resets the selector to Personal" fix covers this context
// for free — one state, one reset path.
export const COLLABORATIONS_CONTEXT = "collaborations";

// One key, namespaced so it never collides with other gallery prefs. Not
// per-user: the switcher already resolves against the live team list on read,
// so a stale id from another account simply falls back to Personal.
export const CONTEXT_STORAGE_KEY = "iyuga.gallery.context";

const safeLocalStorage = () => {
  try {
    // Access can throw in privacy modes / sandboxed iframes.
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_e) {
    return null;
  }
};

// Read the persisted context string, or the Personal sentinel when nothing is
// stored / storage is unavailable. Never throws.
export function readSavedContext() {
  const ls = safeLocalStorage();
  if (!ls) return PERSONAL_CONTEXT;
  try {
    return ls.getItem(CONTEXT_STORAGE_KEY) || PERSONAL_CONTEXT;
  } catch (_e) {
    return PERSONAL_CONTEXT;
  }
}

// Persist the selected context string. Never throws (a failed write just means
// the choice is not remembered next session — non-fatal).
export function saveContext(value) {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(CONTEXT_STORAGE_KEY, String(value ?? PERSONAL_CONTEXT));
  } catch (_e) {
    /* ignore quota / privacy-mode failures */
  }
}

export const isPersonalContext = (value) =>
  value == null || value === PERSONAL_CONTEXT;

export const isCollaborationsContext = (value) =>
  value === COLLABORATIONS_CONTEXT;

// Fired when the gallery context is force-reset to Personal (the Gallery/Home
// nav click below). A saveContext() write alone is not enough: clicking Home
// while already on your own gallery navigates to the SAME route, so the
// already-mounted OwnerView never remounts to re-read storage. Listeners
// (OwnerView) subscribe to this to update their live `selectedContext` state
// too.
export const GALLERY_CONTEXT_RESET_EVENT = "iyuga:gallery-context-reset";

// Reset the persisted context to Personal and notify any mounted gallery.
// Called from the Gallery/Home nav entry (top-nav icon + the responsive
// hamburger/menu variant share the same handler) — never from deep links
// like /teams, which must leave the selector alone.
export function resetToPersonalContext() {
  saveContext(PERSONAL_CONTEXT);
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(GALLERY_CONTEXT_RESET_EVENT));
  }
}

// The integer team id for a team context, or null for Personal / an unparseable
// value. Team ids are positive integers server-side (workspaces PK).
export function teamIdFromContext(value) {
  if (isPersonalContext(value)) return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

// Resolve a (possibly stale) selected/persisted context against the CURRENT
// list of the user's teams. Returns a context guaranteed to be selectable now:
//  - Personal always resolves to Personal,
//  - a team id still in `teams` resolves to that team (as a string),
//  - anything else (left team, garbage, other account's id) -> Personal.
// `teams` is the useMyWorkspaces() payload (array of { id, name, ... }).
export function resolveContext(value, teams) {
  if (isPersonalContext(value)) return PERSONAL_CONTEXT;
  if (isCollaborationsContext(value)) return COLLABORATIONS_CONTEXT;
  const id = teamIdFromContext(value);
  if (id == null) return PERSONAL_CONTEXT;
  const list = Array.isArray(teams) ? teams : [];
  const found = list.some((t) => Number(t?.id) === id);
  return found ? String(id) : PERSONAL_CONTEXT;
}

// Build the ordered switcher options: Personal (labelled "Gallery") first, then (optionally)
// Collaborations, then each team by name. `teams` is the useMyWorkspaces()
// payload; names are trusted server strings. `includeCollaborations` defaults
// false so every existing 1-arg caller is byte-identical
// (GALLERY_COLLABORATIONS_CONTEXT_ENABLED gates it on, same pattern as the
// Teams flag gating the team entries).
export function buildContextOptions(teams, includeCollaborations = false) {
  const list = Array.isArray(teams) ? teams : [];
  return [
    { value: PERSONAL_CONTEXT, label: PERSONAL_CONTEXT_LABEL },
    ...(includeCollaborations
      ? [{ value: COLLABORATIONS_CONTEXT, label: "Collaborations" }]
      : []),
    ...list
      .filter((t) => t && t.id != null)
      .map((t) => ({
        value: String(t.id),
        label: t.name || `Team ${t.id}`,
      })),
  ];
}

// The display name for the currently-selected team context (for the card chip
// and the dropdown button), or null when Personal / not found.
export function teamNameFromContext(value, teams) {
  const id = teamIdFromContext(value);
  if (id == null) return null;
  const list = Array.isArray(teams) ? teams : [];
  const team = list.find((t) => Number(t?.id) === id);
  return team ? team.name || `Team ${id}` : null;
}
